import { useState, useEffect } from 'react'; 
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/AnimatedSection'; 
import { HouseIcon, ShieldCheckIcon, PaintRollerIcon } from '../../components/Icons'; 
import { db } from '../../firebase'; 
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

const HomePage = () => { 
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [heroImageUrl, setHeroImageUrl] = useState("https://placehold.co/1600x900/1e293b/e0f2fe?text=Roof+Construction"); // 기본 이미지 URL

  useEffect(() => {
    // 대표 프로젝트와 배경 이미지를 가져오는 함수
    const fetchHomepageData = async () => {
      setLoadingFeatured(true);
      try {
        const projectsColl = collection(db, "constructionImages");
        
        // 1. 대표 프로젝트 가져오기
        const featuredQuery = query(projectsColl, 
                        where("isFeatured", "==", true), 
                        orderBy("uploadedAt", "desc"), 
                        limit(3)
                      );
        const featuredSnapshot = await getDocs(featuredQuery);
        const projects = featuredSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFeaturedProjects(projects);

        // 2. 배경 이미지 가져오기
        const heroQuery = query(projectsColl, where("isHeroImage", "==", true), limit(1));
        const heroSnapshot = await getDocs(heroQuery);
        if (!heroSnapshot.empty) {
          const heroImage = heroSnapshot.docs[0].data();
          setHeroImageUrl(heroImage.imageUrl);
        }
        // 지정된 이미지가 없으면 기본 URL 유지

      } catch (error) {
        console.error("Error fetching homepage data: ", error);
        setFeaturedProjects([]);
      }
      setLoadingFeatured(false);
    };
    const fetchFeaturedProjects = async () => {
      setLoadingFeatured(true);
      try {
        const projectsColl = collection(db, "constructionImages");
        const q = query(projectsColl, 
                        where("isFeatured", "==", true), 
                        orderBy("uploadedAt", "desc"), 
                        limit(3)
                      );
        const querySnapshot = await getDocs(q);
        const projects = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFeaturedProjects(projects);
      } catch (error) {
        console.error("Error fetching featured projects: ", error);
        setFeaturedProjects([]); 
      }
      setLoadingFeatured(false);
    };

    fetchHomepageData();
    fetchFeaturedProjects();
  }, []);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('our-services');
    if (servicesSection) servicesSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <title>진양건설 - 지붕 공사 및 방수 전문</title>
      <div className="space-y-20 md:space-y-32">
        <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-center justify-center text-center rounded-b-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-sky-600 via-sky-700 to-indigo-800 opacity-90"></div>
          {/* 배경 이미지를 동적으로 설정 */}
          <img 
            src={heroImageUrl} 
            alt="지붕 공사 배경" 
            className="absolute inset-0 w-full h-full object-cover opacity-20" 
            onError={(e) => e.target.src = 'https://placehold.co/1600x900/1e293b/e0f2fe?text=Image+Error'}
          />
          <div className="container mx-auto px-6 relative z-10 text-white">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold mb-6 tracking-tight">
              <span className="block">견고한 지붕,</span>
              <span className="block text-sky-300">안전한 생활의 시작</span>
            </h1>
            <p className="text-lg md:text-2xl mb-10 max-w-3xl mx-auto text-sky-100">
              진양건설은 지붕공사, 방수, 리모델링 전문 기업으로 최고의 기술력과 노하우로 완벽한 시공을 약속합니다.
            </p>
            <Link to="/contact" className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold py-3 px-10 md:py-4 md:px-12 rounded-lg text-lg md:text-xl transition duration-300 transform hover:scale-105 shadow-2xl focus:outline-none focus:ring-4 focus:ring-amber-300 inline-block">
              무료 견적 문의
            </Link>
          </div>
          <button onClick={scrollToServices} className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white animate-bounce hidden md:block p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Scroll to services section"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"></path><path d="m19 12-7 7-7-7"></path></svg></button>
        </section>
        
        <AnimatedSection id="our-services" className="container mx-auto px-6 py-16">
          <div className="text-center mb-16">
            <span className="text-sky-600 dark:text-sky-400 font-semibold uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white mt-2">핵심 서비스 안내</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { title: "지붕 공사 및 리모델링", description: "낡고 오래된 지붕을 최신 자재와 디자인으로 새롭게 바꿔, 건물의 가치와 안전을 동시에 높입니다.", icon: <HouseIcon className="w-10 h-10 text-sky-600 dark:text-sky-400"/>, bgColor: "bg-sky-50 dark:bg-sky-900/50" }, 
              { title: "옥상/외벽 방수 공사", description: "건물 누수의 근본적인 원인을 정확히 진단하고, 완벽한 방수 솔루션을 제공하여 건물을 보호합니다.", icon: <ShieldCheckIcon className="w-10 h-10 text-teal-600 dark:text-teal-400"/>, bgColor: "bg-teal-50 dark:bg-teal-900/50" }, 
              { title: "외벽 및 마감 공사", description: "지붕 공사와 함께 외벽을 리모델링하여 건물의 전체적인 미관과 내구성을 향상시킵니다.", icon: <PaintRollerIcon className="w-10 h-10 text-indigo-600 dark:text-indigo-400"/>, bgColor: "bg-indigo-50 dark:bg-indigo-900/50" }
            ].map((service, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 ${service.bgColor}`}>
                <div className="flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-md">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-800 dark:text-white">{service.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection className="bg-slate-100 dark:bg-slate-800 py-20 md:py-28" animationType="fade-in">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
                <span className="text-amber-500 font-semibold uppercase tracking-wider">Featured Works</span>
                <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white mt-2">대표 시공 사례</h2>
            </div>
            {loadingFeatured ? (
              <div className="text-center text-gray-500 dark:text-gray-400">대표 시공 사례를 불러오는 중...</div>
            ) : featuredProjects.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredProjects.map((project) => (
                  <div key={project.id} className="bg-white dark:bg-slate-700 rounded-xl shadow-lg overflow-hidden group transition-all duration-300 hover:shadow-sky-300/50 dark:hover:shadow-sky-500/30">
                    <Link to={`/projects/gallery/${project.id}`} state={{ photoItem: project, allPhotos: featuredProjects }}>
                      <div className="relative overflow-hidden h-64">
                        <img 
                          src={project.imageUrl} 
                          alt={project.title}
                          draggable="false" // 드래그 방지
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500 ease-out select-none" // 선택 방지
                          style={{ WebkitUserDrag: 'none' }} // Webkit 드래그 방지
                          onError={(e) => e.target.src = 'https://placehold.co/600x450/334155/e0f2fe?text=Project+Error'}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <span className="absolute top-4 left-4 bg-sky-600 text-white text-xs font-semibold px-3 py-1 rounded-full">{project.category}</span>
                      </div>
                    </Link>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-1 text-gray-800 dark:text-white">{project.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">{project.description || '프로젝트 설명이 없습니다.'}</p>
                      <Link 
                        to={`/projects/gallery/${project.id}`} 
                        state={{ photoItem: project, allPhotos: featuredProjects }}
                        className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 font-semibold group-hover:tracking-wider transition-all duration-200"
                      >
                        자세히 보기 &rarr;
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400">선정된 대표 시공 사례가 없습니다.</p>
            )}
          </div>
        </AnimatedSection>
        
        <AnimatedSection className="container mx-auto px-6 py-20 md:py-28 text-center" animationType="scale-up">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 dark:text-white">지붕에 대한 고민, 지금 바로 해결하세요.</h2><p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto">무료 방문 견적부터 책임 시공, 철저한 A/S까지. 진양건설의 전문가가 도와드립니다.</p><Link to="/contact" className="bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white font-bold py-4 px-10 md:px-12 rounded-lg text-lg md:text-xl transition-all duration-300 transform hover:scale-105 shadow-xl focus:outline-none focus:ring-4 focus:ring-sky-300 dark:focus:ring-sky-700 inline-block">무료 견적 문의하기</Link>
        </AnimatedSection>
      </div>
    </>
  );
}
export default HomePage;
